import { useState, useEffect, useRef } from 'react';
import { useLocale } from '../../context';
import { useCasinoBadges } from '../../hooks/useCasinoBadges';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  gameSupportsDemo,
  markGameIconFailed,
  markGameIconLoaded,
  isGameIconLoaded,
  sideGameImageCandidates,
} from '../../data/casinoGames';
import { formatGameDisplayName, splitGameName } from '../../utils/gameCardTheme';
import { claimGameCardOverlay, subscribeGameCardOverlay } from '../../utils/gameCardOverlay';

function SideGameName({ name }) {
  const { primary, secondary } = splitGameName(name);
  const full = [primary, secondary].filter(Boolean).join(' ').trim() || formatGameDisplayName(name);

  if (!full) return null;

  if (!secondary) {
    return (
      <div className="casino-side-game-card__name" lang="en">
        <span className="casino-side-game-card__name-line casino-side-game-card__name-line--solo">{full}</span>
      </div>
    );
  }

  return (
    <div className="casino-side-game-card__name" lang="en">
      <span className="casino-side-game-card__name-line casino-side-game-card__name-line--primary">{primary}</span>
      <span className="casino-side-game-card__name-line casino-side-game-card__name-line--secondary">{secondary}</span>
    </div>
  );
}

function resolveBadges(game) {
  const badges = [];
  if (game.badge) badges.push(game.badge);
  if (game.freespin) badges.push('fs');
  return badges;
}

export default function CasinoSideGameCard({ game, onNavigate }) {
  const _locale = useLocale();
  const tr = _locale.t;
  const { resolveBadgeDisplay } = useCasinoBadges();
  const isTouchUi = useIsMobile();
  const rootRef = useRef(null);
  const overlayIdRef = useRef(`sgc-${game?.id ?? 'x'}-${Math.random().toString(36).slice(2, 8)}`);
  const [visible, setVisible] = useState(false);
  const [candidateIdx, setCandidateIdx] = useState(0);
  const [imgFailed, setImgFailed] = useState(false);
  const [tapOpen, setTapOpen] = useState(false);
  const candidates = sideGameImageCandidates(game, { size: 'md' });
  const imgSrc = candidates[candidateIdx] || candidates[0] || null;
  const [loaded, setLoaded] = useState(() => Boolean(imgSrc && isGameIconLoaded(imgSrc)));
  const badges = resolveBadges(game);
  const showImg = visible && Boolean(imgSrc) && !imgFailed;
  const showDemo = gameSupportsDemo(game);
  // Spinner only while waiting — never swap in _placeholder.webp.
  const showSpinner = !imgFailed && (!loaded || !showImg);

  useEffect(() => {
    setVisible(false);
    setCandidateIdx(0);
    setImgFailed(false);
    setTapOpen(false);
    const first = sideGameImageCandidates(game, { size: 'md' })[0];
    setLoaded(Boolean(first && isGameIconLoaded(first)));
  }, [game?.id]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const scrollRoot = el.closest(
      '.casino-page__aside-scroll, .casino-page__content-scroll, .casino-game-page__side-games-scroll, .mobile-menu-sheet__pane-scroll',
    );
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      {
        root: scrollRoot || null,
        rootMargin: '80px 0px',
        threshold: 0.01,
      },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [game?.id]);

  useEffect(() => {
    if (!isTouchUi) {
      setTapOpen(false);
      return undefined;
    }
    return subscribeGameCardOverlay(overlayIdRef.current, () => setTapOpen(false));
  }, [isTouchUi]);

  useEffect(() => {
    if (!isTouchUi || !tapOpen) return undefined;
    claimGameCardOverlay(overlayIdRef.current);
    const onDoc = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      setTapOpen(false);
    };
    const timer = window.setTimeout(() => {
      document.addEventListener('pointerdown', onDoc, true);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('pointerdown', onDoc, true);
    };
  }, [isTouchUi, tapOpen]);

  const open = (demo) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!game?.id || !onNavigate) return;
    setTapOpen(false);
    onNavigate('casino', { casinoGameId: String(game.id), casinoGameDemo: demo });
  };

  const onCardActivate = (e) => {
    if (e.target.closest('button')) return;
    if (isTouchUi) {
      e.preventDefault();
      e.stopPropagation();
      setTapOpen((openState) => {
        const next = !openState;
        if (next) claimGameCardOverlay(overlayIdRef.current);
        return next;
      });
      return;
    }
    open(false)(e);
  };

  const markLoaded = (el) => {
    if (!el) return;
    if (el.complete && el.naturalWidth > 0) {
      if (imgSrc) markGameIconLoaded(imgSrc);
      setLoaded(true);
    }
  };

  const onImgError = () => {
    setLoaded(false);
    if (!imgSrc) {
      setImgFailed(true);
      return;
    }
    markGameIconFailed(imgSrc);
    const nextIdx = candidateIdx + 1;
    if (nextIdx < candidates.length) {
      setCandidateIdx(nextIdx);
      return;
    }
    setImgFailed(true);
  };

  return (
    <div
      ref={rootRef}
      className={`casino-side-game-card${isTouchUi ? ' casino-side-game-card--mobile-tap' : ''}${tapOpen ? ' is-tap-open' : ''}`}
      role="button"
      tabIndex={0}
      aria-expanded={isTouchUi ? tapOpen : undefined}
      onClick={onCardActivate}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onCardActivate(e); }}
    >
      {badges.length > 0 && (
        <div className="casino-side-game-card__tags">
          {badges.map((badge) => {
            const display = resolveBadgeDisplay(badge);
            if (!display?.label) return null;
            const style = display.color ? { backgroundColor: display.color } : undefined;
            return (
            <span
              key={badge}
              className={`casino-side-game-card__tag${badge === 'fs' ? ' casino-side-game-card__tag--freespin' : ''}`}
              style={style}
            >
              <span className="game-card__tag-text" lang="en">{display.label}</span>
            </span>
            );
          })}
        </div>
      )}
      <div className="casino-side-game-card__media">
        {showSpinner ? (
          <div className="casino-side-game-card__spinner-wrap" aria-hidden="true">
            <span className="casino-side-game-card__spinner" />
          </div>
        ) : null}
        {showImg && (
        <img
          key={imgSrc}
          ref={markLoaded}
          className={`casino-side-game-card__img${loaded ? ' is-loaded' : ''}`}
          src={imgSrc}
          alt=""
          loading="lazy"
          decoding="async"
          fetchPriority="low"
          width={120}
          height={163}
          onLoad={() => {
            if (imgSrc) markGameIconLoaded(imgSrc);
            setLoaded(true);
          }}
          onError={onImgError}
        />
        )}
        <div className="casino-side-game-card__blur" aria-hidden="true" />
        <div className={`casino-side-game-card__overlay${tapOpen ? ' is-visible' : ''}`}>
          {isTouchUi && game?.name ? (
            <div className="casino-side-game-card__overlay-title" lang="en">{game.name}</div>
          ) : null}
          <button type="button" className="casino-side-game-card__play" onClick={open(false)}>{tr('casino.game.play')}</button>
          {showDemo ? (
            <button type="button" className="casino-side-game-card__demo" onClick={open(true)}>{tr('casino.game.demo')}</button>
          ) : null}
        </div>
        <div className="casino-side-game-card__label">
          <div className="casino-side-game-card__caption">
            <SideGameName name={game.name} />
            <div className="casino-side-game-card__provider">{game.provider}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
